import { memo, useState } from 'react';
import { useRouter } from 'next/router';

import { Checkbox, FieldSet, Label, LabeledControl, Legend, Toast, useToast } from '@zillow/constellation-10';

import { FeedOverrideSection } from '@zg-rentals/ts-rental-manager-api-client';
import {
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasCityView,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasGolfCourseView,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasMountainView,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasParkView,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasTerritorialView,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasWaterView,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasWoodedView,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';

import { amenityPatches } from '../../../../hooks/queries/listingDetailsCache';
import { useGetListingDetails } from '../../../../hooks/queries/properties';
import { useBatchMutationToast } from '../../../../hooks/useBatchMutationToast';
import { useFeedAmenityMutation } from '../../../../hooks/useFeedAmenityMutation';
import { useIsInputFormDisabled } from '../../../../hooks/useIsInputFormDisabled';
import { usePropertyEditMutation } from '../../../../hooks/usePropertyEditMutation';

import { VIEW_KEYS, VIEW_METADATA, type ViewKey } from '../../../../constants/KeyFeaturesConstants';

import { useAuth } from '../../../AuthProvider';

import { type AmenityFlagSave } from '../../utils';

type LocalState = Record<ViewKey, boolean>;

const ViewsAmenities = (): JSX.Element | null => {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueToast } = useToast();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const viewTypes = listingDetails?.buildingAttributes?.viewTypes;
  const canUpdateListing = user?.features?.canUpdateListing ?? false;

  const [localState, setLocalState] = useState<LocalState>({
    hasCity: viewTypes?.hasCity ?? false,
    hasGolfCourse: viewTypes?.hasGolfCourse ?? false,
    hasMountain: viewTypes?.hasMountain ?? false,
    hasPark: viewTypes?.hasPark ?? false,
    hasTerritorial: viewTypes?.hasTerritorial ?? false,
    hasWater: viewTypes?.hasWater ?? false,
    hasWooded: viewTypes?.hasWooded ?? false,
  });

  const { startRequest, handleSuccess, handleError } = useBatchMutationToast({
    successMessage: 'Views updated',
  });

  const isInputFormDisabled = useIsInputFormDisabled(listingDetails, canUpdateListing, FeedOverrideSection.AMENITIES);
  const { isFeedListing, setFeedAmenity } = useFeedAmenityMutation();

  const { mutateAsync: updateCity } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasCityView,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: { api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasCityView', details: { listingAlias } },
    },
  });

  const { mutateAsync: updateGolfCourse } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasGolfCourseView,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasGolfCourseView',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateMountain } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasMountainView,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasMountainView',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updatePark } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasParkView,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: { api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasParkView', details: { listingAlias } },
    },
  });

  const { mutateAsync: updateTerritorial } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasTerritorialView,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasTerritorialView',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateWater } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasWaterView,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: { api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasWaterView', details: { listingAlias } },
    },
  });

  const { mutateAsync: updateWooded } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasWoodedView,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasWoodedView',
        details: { listingAlias },
      },
    },
  });

  const mutationMap = {
    hasCity: updateCity,
    hasGolfCourse: updateGolfCourse,
    hasMountain: updateMountain,
    hasPark: updatePark,
    hasTerritorial: updateTerritorial,
    hasWater: updateWater,
    hasWooded: updateWooded,
  } as unknown as Record<keyof LocalState, AmenityFlagSave>;

  const handleCheckboxChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fieldName = event.target.name as ViewKey;
    const checked = event.target.value === 'true';
    const previousValue = !checked;

    startRequest();
    setLocalState((prev) => ({ ...prev, [fieldName]: checked }));

    try {
      const metadata = VIEW_METADATA[fieldName];
      if (isFeedListing) {
        await setFeedAmenity(metadata.apiKey, checked);
      } else {
        await mutationMap[fieldName]({
          postData: {
            [metadata.apiKey]: checked,
            listingAlias,
          },
        });
      }
      handleSuccess();
    } catch (err) {
      handleError();
      setLocalState((prev) => ({ ...prev, [fieldName]: previousValue }));
      const errorMessage = DatarouterError.getDisplayMessage(
        err,
        'Something went wrong, refresh the page and try again',
      );
      enqueueToast(<Toast tone="critical">{errorMessage}</Toast>);
    }
  };

  return (
    <FieldSet legend={<Legend>Views</Legend>} disabled={isInputFormDisabled}>
      {VIEW_KEYS.map((fieldName) => {
        const metadata = VIEW_METADATA[fieldName];
        return (
          <LabeledControl
            key={`viewTypes-${metadata.label}`}
            label={<Label>{metadata.label}</Label>}
            control={
              <Checkbox
                name={fieldName}
                value={String(!localState[fieldName])}
                checked={localState[fieldName]}
                onChange={handleCheckboxChange}
              />
            }
          />
        );
      })}
    </FieldSet>
  );
};

export default memo(ViewsAmenities);
